import { Checkbox, Col, Form, Input, InputNumber, InputNumberProps, Row, Select } from 'antd';
import React from 'react';

import {
	BCODER3,
	BadgeStudioBarCode,
	BadgeStudioImage,
	BadgeStudioMagstripe,
	C_ObjectType,
	C_PictureTypeCustom,
	GenericGraphicObject,
	GraphicCommandSet,
	GraphicDirectoryInfo,
	GraphicLink,
	GraphicText,
	ObjectFontProperties,
	PointMonitoringZone,
	VelocityObject,
} from '../../../model/GraphicModel';

import { DefaultOptionType } from 'antd/es/select';
import { deviceAdminApi, graphicApi } from '../../../api';
import { ResponseStatusCode } from '../../../model/CommonModel';
import { SimpleCommandSet } from '../../../model/DeviceAdminModel';
import { UserDefinedFieldSetup } from '../../../model/UserDefinedFields';
import { CustomColorPicker } from './CustomColorPicker/CustomColorPicker';
import styles from './propertiesWindow.module.scss';

type ASMUColorRowsProps = {
	alarmColor?: string;
	alarmId?: string;
	alarmTitle?: string;
	enableAlarm?: boolean;
	enableMasked?: boolean;
	enableSecure?: boolean;
	enableUnknown?: boolean;
	onChangeColorPicker?: (inputName: string[], value: string) => void;
	maskedColor?: string;
	maskedId?: string;
	maskedTitle?: string;
	secureId?: string;
	secureTitle?: string;
	secureColor?: string;
	unknownColor?: string;
	unknownId?: string;
	unknownTitle?: string;
};

type PupulateCustomDataProps = {
	customOpts: string[];
	type: number;
	udfs: UserDefinedFieldSetup[];
};

export enum InputType {
	Checkbox = 0,
	Input,
	InputNumber,
	Select,
}

type BuildRowProps = {
	colSpan?: number[];
	disabled?: boolean;
	inputName: string | string[];
	inputType?: InputType;
	inputValue?: string | number | boolean;
	isTitleRow?: boolean;
	label: string;
	labelTitle?: string;
	limits?: number[];
	opts?: DefaultOptionType[];
	rowGutter?: number;
	step?: string;
};

type BuildFontRowsProps = {
	fontInfo: Partial<ObjectFontProperties>;
	fontFamilies: string[];
	isFontStyleEnabled: boolean;
	onChangeColorPicker: (inputName: string[], value: string) => void;
};

export const buildRows = (dataRows: BuildRowProps[]): JSX.Element[] => {
	const rows: JSX.Element[] = [];
	dataRows.forEach(dataRow => rows.push(buildRow(dataRow)));
	return rows;
};

export const buildRow = ({
	inputType,
	inputValue,
	isTitleRow,
	label,
	limits,
	inputName,
	labelTitle,
	colSpan,
	disabled,
	opts,
	rowGutter,
	step,
}: BuildRowProps) => {
	let row: JSX.Element = null;
	const rowId: string = Array.isArray(inputName) ? inputName.join('_') : inputName;
	if (isTitleRow) {
		return (
			<Row gutter={rowGutter || 8} className={styles.rowTitle} key={rowId}>
				<span>{label}</span>
			</Row>
		);
	}
	const labelProps: React.HTMLAttributes<HTMLSpanElement | HTMLLabelElement> = {};
	if (labelTitle) {
		labelProps.title = labelTitle;
	}

	let input: JSX.Element;
	switch (inputType) {
		case InputType.Checkbox:
			input = (
				<Form.Item
					initialValue={inputValue as boolean}
					name={inputName}
					rules={[{ required: true, message: 'Required field' }]}
					validateTrigger='onBlur'
					valuePropName='checked'>
					<Checkbox disabled={disabled} />
				</Form.Item>
			);
			break;
		case InputType.Select:
			input = (
				<Form.Item initialValue={inputValue} name={inputName} rules={[{ required: true, message: 'Required field' }]} validateTrigger='onBlur'>
					<Select disabled={disabled} options={opts || []} />
				</Form.Item>
			);
			break;
		case InputType.InputNumber:
			const newLimits: InputNumberProps = {
				min: limits?.[0] || 0,
				step,
			};
			if (limits?.length > 0) {
				newLimits.max = limits[1];
			}
			input = (
				<Form.Item
					initialValue={inputValue as number}
					name={inputName}
					rules={[{ required: true, message: 'Required field' }]}
					validateTrigger='onBlur'>
					<InputNumber {...newLimits} />
				</Form.Item>
			);
			break;
		default:
			input = (
				<Form.Item initialValue={inputValue} name={inputName} rules={[{ required: true, message: 'Required field' }]} validateTrigger='onBlur'>
					<Input disabled={disabled} />
				</Form.Item>
			);
	}

	row = (
		<Row gutter={rowGutter || 8} key={rowId}>
			<Col span={colSpan?.[0] || 8}>
				<label htmlFor={rowId} {...labelProps}>
					{label}
				</label>
			</Col>
			<Col span={colSpan?.[1] || 16}>{input}</Col>
		</Row>
	);
	return row;
};

export const buildASMUColorRows = ({
	alarmColor,
	alarmId,
	alarmTitle,
	enableAlarm,
	enableMasked,
	enableSecure,
	enableUnknown,
	onChangeColorPicker,
	maskedColor,
	maskedId,
	maskedTitle,
	secureColor,
	secureId,
	secureTitle,
	unknownColor,
	unknownId,
	unknownTitle,
}: ASMUColorRowsProps): JSX.Element[] => {
	const rows: JSX.Element[] = [];

	if (enableAlarm) {
		rows.push(
			<Row gutter={8} key={alarmId}>
				<Col className={styles.colFlex} span={8}>
					<label htmlFor={alarmId} title={alarmTitle}>
						{_('AlarmColor')}
					</label>
				</Col>
				<Col className={styles.colFlex} span={16}>
					<Form.Item
						className={styles.formItemColorPicker}
						name={alarmId}
						rules={[{ required: true, message: 'Required field' }]}
						validateTrigger='onBlur'>
						<CustomColorPicker name={[alarmId]} initialColor={alarmColor} onChangeColorPicker={onChangeColorPicker} />
					</Form.Item>
				</Col>
			</Row>
		);
	}

	if (enableSecure) {
		rows.push(
			<Row gutter={8} key={secureId}>
				<Col className={styles.colFlex} span={8}>
					<label htmlFor={secureId} title={secureTitle}>
						{_('SecureColor')}
					</label>
				</Col>
				<Col className={styles.colFlex} span={16}>
					<Form.Item
						className={styles.formItemColorPicker}
						name={secureId}
						rules={[{ required: true, message: 'Required field' }]}
						validateTrigger='onBlur'>
						<CustomColorPicker name={[secureId]} initialColor={secureColor} onChangeColorPicker={onChangeColorPicker} />
					</Form.Item>
				</Col>
			</Row>
		);
	}

	if (enableMasked) {
		rows.push(
			<Row gutter={8} key={maskedId}>
				<Col className={styles.colFlex} span={8}>
					<label htmlFor={maskedId} title={maskedTitle}>
						{_('MaskedColor')}
					</label>
				</Col>
				<Col className={styles.colFlex} span={16}>
					<Form.Item
						className={styles.formItemColorPicker}
						name={maskedId}
						rules={[{ required: true, message: 'Required field' }]}
						validateTrigger='onBlur'>
						<CustomColorPicker name={[maskedId]} initialColor={maskedColor} onChangeColorPicker={onChangeColorPicker} />
					</Form.Item>
				</Col>
			</Row>
		);
	}

	if (enableUnknown) {
		rows.push(
			<Row gutter={8} key={unknownId}>
				<Col className={styles.colFlex} span={8}>
					<label htmlFor={unknownId} title={unknownTitle}>
						{_('UnknownColor')}
					</label>
				</Col>
				{/* .ObjTags("SYS_PMZ_UnknownColor") */}
				<Col className={styles.colFlex} span={16}>
					<Form.Item
						className={styles.formItemColorPicker}
						name={unknownId}
						rules={[{ required: true, message: 'Required field' }]}
						validateTrigger='onBlur'>
						<CustomColorPicker name={[unknownId]} initialColor={unknownColor} onChangeColorPicker={onChangeColorPicker} />
					</Form.Item>
				</Col>
			</Row>
		);
	}
	return rows;
};

export const buildNameRow = (nameValue: string, disabled: boolean): JSX.Element => {
	return (
		<Row gutter={8} key='Name'>
			<Col span={8}>
				<label htmlFor='Name'>{_('Name')}</label>
			</Col>
			<Col span={16}>
				<Form.Item initialValue={nameValue} name='Name' rules={[{ required: true, message: 'Required field' }]} validateTrigger='onBlur'>
					{/* .ObjTags("Name") */}
					<Input disabled={disabled} />
				</Form.Item>
			</Col>
		</Row>
	);
};

export const buildFontRows = ({ fontInfo, fontFamilies, onChangeColorPicker, isFontStyleEnabled }: BuildFontRowsProps): JSX.Element[] => {
	const { FontFamily, FontStyle, TextColorHex } = fontInfo;
	const rows: JSX.Element[] = [];
	const rowId: string = 'pFont';

	let fontFamilyValue = fontFamilies[0];
	if (fontFamilies.indexOf(FontFamily) > -1) {
		fontFamilyValue = FontFamily;
	}

	rows.push(
		<Row gutter={8} className={styles.rowTitle} key='fontTitle'>
			<span>{_('Font')}</span>
		</Row>,
		<Row gutter={8} key={rowId}>
			{/* .ft ??? Where is this comming from? */}
			<Col span={13}>
				<Form.Item
					initialValue={fontFamilyValue}
					name={['FontInfo', 'FontFamily']}
					rules={[{ required: true, message: 'Required field' }]}
					validateTrigger='onBlur'>
					<Select
						options={fontFamilies.map(f => ({
							label: f,
							value: f,
						}))}
					/>
				</Form.Item>
			</Col>
			{/* .TextColor */}
			<Col className={styles.colFlex} span={11}>
				<Form.Item
					className={styles.formItemColorPicker}
					name={['FontInfo', 'TextColorHex']}
					rules={[{ required: true, message: 'Required field' }]}
					validateTrigger='onBlur'>
					<CustomColorPicker name={['FontInfo', 'TextColorHex']} initialColor={TextColorHex} onChangeColorPicker={onChangeColorPicker} />
				</Form.Item>
			</Col>
		</Row>
	);

	if (isFontStyleEnabled) {
		rows.push(
			<Row gutter={8} key='pTextStyle'>
				{/* .TextStyle */}
				<Col span={24}>
					<Form.Item
						initialValue={FontStyle}
						name={['FontInfo', 'FontStyle']}
						rules={[{ required: true, message: 'Required field' }]}
						validateTrigger='onBlur'>
						<Select>
							<Select.Option key={0} value={0}>
								{_('Standard')}
							</Select.Option>
							<Select.Option value={1}>{_('Bounded')}</Select.Option>
							<Select.Option value={2}>{_('Multiline')}</Select.Option>
							<Select.Option value={3}>{_('Boxed')}</Select.Option>
						</Select>
					</Form.Item>
				</Col>
			</Row>
		);
	}
	return rows;
};

export const pupulateCustomData = async ({ customOpts, type, udfs }: PupulateCustomDataProps): Promise<DefaultOptionType[]> => {
	const opts: DefaultOptionType[] = [];

	const matrixData: string[][] = [];
	let i: number = 0;
	opts.push(addCustomData(i, `<${_('None')}>`, '', matrixData));
	customOpts.forEach(displayValue => {
		opts.push(addCustomData(++i, displayValue, displayValue, matrixData));
	});

	opts.push(addCustomData(++i, _('Title'), '%<Title>%', matrixData));
	opts.push(addCustomData(++i, _('FirstName'), '%<First Name>%', matrixData));
	opts.push(addCustomData(++i, _('MiddleName'), '%<Middle Name>%', matrixData));
	opts.push(addCustomData(++i, _('LastName'), '%<Last Name>%', matrixData));
	opts.push(addCustomData(++i, _('Suffix'), '%<Suffix>%', matrixData));
	opts.push(addCustomData(++i, _('HostActivationDate'), '%<Host Activation Date>%', matrixData));
	opts.push(addCustomData(++i, _('HostExpirationDate'), '%<Host Expiration Date>%', matrixData));
	opts.push(addCustomData(++i, _('HostExpirationUsed'), '%<Host Expiration Used>%', matrixData));
	opts.push(addCustomData(++i, _('HostDescription'), '%<Host Description>%', matrixData));
	opts.push(addCustomData(++i, _('HostIssueNumber'), '%<Host Issue Number>%', matrixData));
	opts.push(addCustomData(++i, _('IssueControlCount'), '%<Issue Control Count>%', matrixData));
	opts.push(addCustomData(++i, _('PrintControlCount'), '%<Print Control Count>%', matrixData));
	opts.push(addCustomData(++i, _('HostID'), '%<Host ID>%', matrixData));
	opts.push(addCustomData(++i, _('CredentialID'), '%<Credential ID>%', matrixData));
	opts.push(addCustomData(++i, `${_('Stamp')}#`, '%<HostCardStamp>%', matrixData));
	opts.push(addCustomData(++i, _('CardData'), '%<HostCardData>%', matrixData));
	opts.push(addCustomData(++i, _('MATCH'), '%<MatchCode>%', matrixData));

	// const udfs: UserDefinedFieldModel = await graphicApi.getUdfs();
	udfs.forEach(udf => {
		const str: string = `${_('UDF')}${udf.FieldNumber.toString().padStart(2, '0')} - ${udf.Caption}`;
		opts.push(addCustomData(++i, str, `%<UDF${udf.FieldNumber}>%`, matrixData));
	});

	// TODO: Complete this logic...
	switch (type) {
		case 1:
			// aCustomData_DataField = aCustomData
			break;
		case 2:
			// aCustomData_MagStipeData = aCustomData
			break;
		case 3:
			// aCustomData_BarcodeData = aCustomData
			break;
		case 4:
			// aCustomData_BarcodeComment = aCustomData
			break;
		default:
		// aCustomData_BarcodeSupplemental = aCustomData
	}

	return opts;
};

const addCustomData = (idx: number, displayValue: string, data: string, matrixData: string[][]): DefaultOptionType => {
	let opt: DefaultOptionType = {
		label: displayValue,
		value: idx,
	};
	matrixData.push([displayValue, data]);
	return opt;
};

export const getBarcodeReductionOpts = (narrowBarWidth: number): DefaultOptionType[] => {
	const opts: DefaultOptionType[] = [];
	for (let i = -15; i <= 15; i++) {
		const calculatedI = i * 5;
		opts.push({
			label: `${calculatedI} - ${calculatedI.toString().padStart(2, '00')}% (${((narrowBarWidth * calculatedI) / 100).toFixed(2)} mils)`,
			value: i * 5,
		});
	}
	return opts;
};

export const getObjectTypeSelectOpts = (): DefaultOptionType[] => {
	return [
		{
			label: _('Arc'),
			value: C_PictureTypeCustom.MDOT_ARC,
		},
		{
			label: _('Bezier'),
			value: C_PictureTypeCustom.MDOT_BEZIER,
		},
		{
			label: _('Bitmap'),
			value: C_PictureTypeCustom.MDOT_BITMAP,
		},
		{
			label: _('Chord'),
			value: C_PictureTypeCustom.MDOT_CHORD,
		},
		{
			label: _('Container'),
			value: C_PictureTypeCustom.MDOT_CONTAINER,
		},
		{
			label: _('DimensionLine'),
			value: C_PictureTypeCustom.MDOT_DIMLINE,
		},
		{
			label: _('Ellipse'),
			value: C_PictureTypeCustom.MDOT_ELLIPSE,
		},
		{
			label: _('FloodFill'),
			value: C_PictureTypeCustom.MDOT_FLOODFILL,
		},
		{
			label: _('Image'),
			value: C_PictureTypeCustom.MDOT_IMAGE,
		},
		{
			label: _('Line'),
			value: C_PictureTypeCustom.MDOT_LINE,
		},
		{
			label: _('Link'),
			value: C_PictureTypeCustom.MDOT_LINK,
		},
		{
			label: _('Sector'),
			value: C_PictureTypeCustom.MDOT_PIE,
		},
		{
			label: _('Polygon'),
			value: C_PictureTypeCustom.MDOT_POLYGON,
		},
		{
			label: _('Polyline'),
			value: C_PictureTypeCustom.MDOT_POLYLINE,
		},
		{
			label: _('Polylink'),
			value: C_PictureTypeCustom.MDOT_POLYLINK,
		},
		{
			label: _('Polypolygon'),
			value: C_PictureTypeCustom.MDOT_POLYPOLYGON,
		},
		{
			label: _('Rectangle'),
			value: C_PictureTypeCustom.MDOT_RECTANGLE,
		},
		{
			label: _('RoundedRectangle'),
			value: C_PictureTypeCustom.MDOT_ROUNDRECT,
		},
		{
			label: _('SolidPicture'),
			value: C_PictureTypeCustom.MDOT_SOLIDPICTURE,
		},
		{
			label: _('Text'),
			value: C_PictureTypeCustom.MDOT_TEXT,
		},
		{
			label: _('DataboundBarcode'),
			value: C_PictureTypeCustom.MDOT_DBBARCODE,
		},
		{
			label: _('DataboundImage'),
			value: C_PictureTypeCustom.MDOT_DBIMAGE,
		},
		{
			label: _('DataboundMagstripe'),
			value: C_PictureTypeCustom.MDOT_DBMAGSTRIPE,
		},
		{
			label: _('DataboundText'),
			value: C_PictureTypeCustom.MDOT_DBTEXT,
		},
		{
			label: _('PhysicalZone'),
			value: C_PictureTypeCustom.MDOT_PHYSICALZONE,
		},
		{
			label: _('ControlZone'),
			value: C_PictureTypeCustom.MDOT_CONTROLZONE,
		},
		{
			label: _('VIO'),
			value: C_PictureTypeCustom.MDOT_VIO,
		},
		{
			label: _('CommandSet'),
			value: C_PictureTypeCustom.MDOT_COMMANDSET,
		},
		{
			label: _('PointMonitoringZone'),
			value: C_PictureTypeCustom.MDOT_PMZ,
		},
		/* {
			label: _(''),
			value: C_PictureTypeCustom
		}, */
		{
			label: _('CustomLink'),
			value: C_PictureTypeCustom.MDOT_CUSTOMLINK,
		},
	];
};

export const getUDFObjectPropertyData = async (sectionsMap: Map<string, JSX.Element[]>, sysUDF: string, udfs: UserDefinedFieldSetup[]) => {
	const opts: DefaultOptionType[] = [
		{
			label: `<${_('None')}>`,
			value: 0,
		},
	];

	udfs.forEach(udf => {
		opts.push({
			label: udf.Caption,
			value: udf.FieldNumber,
		});
	});

	sectionsMap.set(_('General'), [
		...(sectionsMap.get(_('General')) || []),
		buildRow({
			label: _('UDF'),
			inputName: 'UDF',
			inputType: InputType.Select,
			inputValue: sysUDF, // .ObjTags("SYS_UDF")
			labelTitle: _('PUDFDesc'),
			opts,
		}),
	]);
};

type BuildMDObjectInputsParams = {
	currentObject:
		| GenericGraphicObject
		| GraphicText
		| VelocityObject
		| PointMonitoringZone
		| GraphicCommandSet
		| GraphicLink
		| BadgeStudioImage
		| BadgeStudioBarCode
		| BadgeStudioMagstripe;
	isBadge: boolean;
	fontFamilies?: string[];
	sectionsMap: Map<string, JSX.Element[]>;
	tempGenericObject: GenericGraphicObject;
	onChangeColorPicker: (inputName: string[], value: string) => void;
	udfs?: UserDefinedFieldSetup[];
	udfs256?: UserDefinedFieldSetup[];
};

export const buildMDObjectInputs = async ({
	currentObject,
	onChangeColorPicker,
	isBadge,
	fontFamilies = [],
	sectionsMap,
	tempGenericObject,
	udfs,
	udfs256,
}: BuildMDObjectInputsParams) => {
	const isNameDisabled: boolean = currentObject.ObjType === C_PictureTypeCustom.MDOT_VIO;
	sectionsMap.set(_('General'), [buildNameRow(tempGenericObject.Name, isNameDisabled)]);

	/* TODO: Is this going to be also in WEB?
	const m_LogCoords: boolean = false;
	if (m_LogCoords) {
		sectionsMap.set(_('Debug'), [
			...(sectionsMap.get(_('General')) || []),
			buildRow({
				inputType: InputType.Input,
				inputValue: '', // TODO: .ObjTags("SYS_DebugLogCoords")
				label: _('LogCoords'),
				rowId: 'SysDebugLogCoords',
				labelTitle: _('LogCoordsDesc'),
				colSpan: [8, 16],
				rowGutter: 8,
			}),
		]);
	}
	// TODO: if(m_Canvases.CanvasType(m_Canvases.IndexForMDIndex(m_CurrMD)) === CanvasTypes.PTVIO)
	const showAlarmFlashRow: boolean = false;
	if (showAlarmFlashRow) {
		sectionsMap.set(_('General'), [
			...(sectionsMap.get(_('General')) || []),
			buildRow({
				inputType: InputType.Input,
				inputValue: '', // TODO: .ObjTags("SYS_AlarmFlash")
				label: _('AlarmFlash'),
				rowId: 'SysAlarmFlash',
			}),
		]);
	}
	*/

	sectionsMap.set(_('General'), [
		...(sectionsMap.get(_('General')) || []),
		buildRow({
			inputType: InputType.Select,
			inputValue: currentObject.ObjType,
			label: _('ObjectType'),
			inputName: 'ObjType',
			labelTitle: _('ObjectTypeDesc'),
			disabled: true,
			opts: getObjectTypeSelectOpts(),
		}),
	]);

	sectionsMap.set(_('Appearance'), [
		buildRow({
			inputType: InputType.Checkbox,
			inputValue: /*tempGenericObject.Visible*/ true, // MDraw(m_CurrMD).ObjVisible
			label: _('Visible'),
			inputName: 'Visible',
			labelTitle: _('VisibleDesc'),
		}),
	]);

	switch (currentObject.ObjType) {
		case C_PictureTypeCustom.MDOT_LINK: {
			const newObject = currentObject as GraphicLink;

			sectionsMap.set(_('General'), [
				...(sectionsMap.get(_('General')) || []),
				buildRow({
					label: _('Text'),
					inputName: 'DisplayText',
					inputType: InputType.Input,
					inputValue: newObject.FontInfo.DisplayText, // .Text
				}),
			]);

			const mapsResponse = await graphicApi.getAllMaps();
			let maps: GraphicDirectoryInfo[] = [];
			if (mapsResponse.ResponseStatusCode === ResponseStatusCode.Success) {
				maps = mapsResponse.Entity;
			}

			const opts: DefaultOptionType[] = maps.map(map => ({
				label: map.Name,
				value: map.Id,
			}));

			sectionsMap.set(_('Data'), [
				buildRow({
					label: _('Link'),
					inputName: 'SysLinkData',
					inputType: InputType.Select,
					inputValue: newObject.LinkId, // .ObjTags("SYS_LinkData")
					labelTitle: _('LinkDesc'),
					opts,
				}),
			]);

			// Retrieving font info
			const { Id, FontFamily, FontSize, FontStyle, TextColorHex, AutoSize } = newObject.FontInfo;
			sectionsMap.set(_('Appearance'), [
				...sectionsMap.get(_('Appearance')),
				...buildFontRows({
					fontInfo: {
						Id,
						FontFamily,
						FontSize,
						FontStyle,
						TextColorHex,
						AutoSize,
					},
					fontFamilies,
					isFontStyleEnabled: true,
					onChangeColorPicker,
				}),
			]);

			break;
		}
		case C_PictureTypeCustom.MDOT_COMMANDSET: {
			const newObject = currentObject as GraphicCommandSet;

			const commandsetsResponse = await deviceAdminApi.getSimpleCommandSets();
			let commandSets: SimpleCommandSet[] = [];
			if (commandsetsResponse.ResponseStatusCode === ResponseStatusCode.Success) {
				commandSets = commandsetsResponse.Entity;
			}

			const opts: DefaultOptionType[] = commandSets.map(cs => ({
				label: cs.Name,
				value: `${cs.Type}:${cs.CommandSetId}`, // TODO: probably this can be only Id because I think DTII Commandsets are not used anymore
			}));

			sectionsMap.set(_('Data'), [
				buildRow({
					label: _('CommandSet'),
					inputName: 'SysCommandSetData',
					inputType: InputType.Select,
					inputValue: newObject.CommandSetData, // .ObjTags("SYS_CommandSetData")
					labelTitle: _('CommandSetDesc'),
					opts,
				}),
			]);

			let isFontStyleEnabled: boolean = false;

			if (newObject.ObjType === C_ObjectType.OT_TEXT) {
				sectionsMap.set(_('General'), [
					...sectionsMap.get(_('General')),
					buildRow({
						label: _('Text'),
						inputName: 'DisplayText',
						inputValue: newObject.FontInfo.DisplayText, // .Text
					}),
				]);
				isFontStyleEnabled = true;
			}

			// Retrieving font info
			const { Id, FontFamily, FontSize, FontStyle, TextColorHex, AutoSize } = newObject.FontInfo;
			sectionsMap.set(_('Appearance'), [
				...sectionsMap.get(_('Appearance')),
				...buildFontRows({
					fontInfo: {
						Id,
						FontFamily,
						FontSize,
						FontStyle,
						TextColorHex,
						AutoSize,
					},
					fontFamilies,
					isFontStyleEnabled,
					onChangeColorPicker,
				}),
			]);

			break;
		}
		case C_PictureTypeCustom.MDOT_CUSTOMLINK: {
			/*  TODO: Check how to implement this logic
				Set mp.ExternalControl = UCustomLink
				UCustomLink.CustomLinkID = IIf((.ObjTags("SYS_CustomLinkData") = ""), 0, .ObjTags("SYS_CustomLinkData"))
				UCustomLink.CustomLinkDisplayName = .ObjTags("SYS_CustomLinkDisplayName")
			*/

			const newObject = currentObject as GraphicLink;

			sectionsMap.set(_('General'), [
				...sectionsMap.get(_('General')),
				buildRow({
					label: _('Custom'),
					inputName: 'SysCustomLinkData',
					inputType: InputType.Input,
					inputValue: '', // TODO: Check how to implement this logic
				}),
			]);

			if (newObject.ObjType === C_ObjectType.OT_TEXT) {
				sectionsMap.set(_('General'), [
					...sectionsMap.get(_('General')),
					buildRow({
						label: _('Text'),
						inputName: 'DisplayText',
						inputValue: newObject.FontInfo.DisplayText,
					}),
				]);

				// Retrieving font info
				const { Id, FontFamily, FontSize, FontStyle, TextColorHex, AutoSize } = newObject.FontInfo;
				sectionsMap.set(_('Appearance'), [
					...sectionsMap.get(_('Appearance')),
					...buildFontRows({
						fontInfo: {
							Id,
							FontFamily,
							FontSize,
							FontStyle,
							TextColorHex,
							AutoSize,
						},
						fontFamilies,
						isFontStyleEnabled: true,
						onChangeColorPicker,
					}),
				]);
			} else {
				const { FontFamily } = newObject.FontInfo;
				let fontFamilyValue = fontFamilies[0];
				if (fontFamilies.indexOf(FontFamily) > -1) {
					fontFamilyValue = FontFamily;
				}

				sectionsMap.set(_('Appearance'), [
					...sectionsMap.get(_('Appearance')),
					buildRow({
						label: _('Font'),
						inputName: ['FontInfo', 'FontFamily'],
						inputType: InputType.Select,
						inputValue: fontFamilyValue, // currentObject.FontFamily
						opts: fontFamilies.map(f => ({
							label: f,
							value: f,
						})),
					}),
				]);
			}

			break;
		}
		case C_PictureTypeCustom.MDOT_DBBARCODE: {
			const newObject = currentObject as BadgeStudioBarCode;
			const { BarcodeFont: FontFamily } = newObject;
			let fontFamilyValue = fontFamilies[0];
			if (fontFamilies.indexOf(FontFamily) > -1) {
				fontFamilyValue = FontFamily;
			}

			sectionsMap.set(_('Appearance'), [
				...(sectionsMap.get(_('Appearance')) || []),
				buildRow({
					label: _('Font'),
					inputName: 'fontTitle',
					isTitleRow: true,
				}),
				<Row gutter={8} key='pBarcodeFont'>
					{/* .ObjTags("SYS_BarFont") */}
					<Col span={13}>
						<Form.Item
							name={['FontInfo', 'FontFamily']}
							initialValue={fontFamilyValue}
							rules={[{ required: true, message: 'Required field' }]}
							validateTrigger='onBlur'>
							<Select
								options={fontFamilies.map(f => ({
									label: f,
									value: f,
								}))}
							/>
						</Form.Item>
					</Col>
					{/* .ObjTags("SYS_BarFontColor") */}
					<Col className={styles.colFlex} span={11}>
						<Form.Item
							className={styles.formItemColorPicker}
							name={['FontInfo', 'TextColorHex']}
							rules={[{ required: true, message: 'Required field' }]}
							validateTrigger='onBlur'>
							<CustomColorPicker
								name={['FontInfo', 'TextColorHex']}
								initialColor={newObject.BarcodeFontColor}
								onChangeColorPicker={onChangeColorPicker}
							/>
						</Form.Item>
					</Col>
				</Row>,
				buildRow({
					inputName: 'BackgroundStyle',
					label: _('BarcodeBackgroundStyle'),
					inputType: InputType.Checkbox,
					inputValue: newObject.BarcodeBackgroundStyle,
					colSpan: [16, 8],
				}),
			]);

			let customOpts: string[] = [];
			const customOptsResp = await graphicApi.getCustomBadgeFields();
			if (customOptsResp.ResponseStatusCode === ResponseStatusCode.Success) {
				customOpts = customOptsResp.Entity;
			}

			/* let udfs: UserDefinedFieldSetup[] = [];
			const udfsResponse = await enrollmentApi.getUserDefinedFields();
			if (udfsResponse.ResponseStatusCode === ResponseStatusCode.Success) {
				udfs = udfsResponse.Entity;
			} */

			sectionsMap.set(_('BarCode'), [
				...buildRows([
					{
						label: _('BarcodeSymbology'),
						inputName: 'Symbology',
						labelTitle: _('BarcodeSymbologyDecs'),
						inputType: InputType.Select,
						inputValue: newObject.Symbology, // .ObjTags("SYS_BarSymbology")
						opts: [
							{
								label: `<${_('Code39Normal')}>`,
								value: BCODER3.Code39,
							},
							{
								label: `<${_('Code39ASCII')}>`,
								value: BCODER3.FullASCII,
							},
							{
								label: `<${_('UPCA')}>`,
								value: BCODER3.UPCa,
							},
							{
								label: `<${_('UPCE')}>`,
								value: BCODER3.UPCe,
							},
							{
								label: `<${_('EAN8')}>`,
								value: BCODER3.EAN8,
							},
							{
								label: `<${_('EAN13')}>`,
								value: BCODER3.EAN13,
							},
							{
								label: `<${_('BookLan')}>`,
								value: BCODER3.BookLan,
							},
							{
								label: `<${_('Interleaved2Of5')}>`,
								value: BCODER3.I2of5,
							},
							{
								label: `<${_('CodaBar')}>`,
								value: BCODER3.CodaBar,
							},
							{
								label: `<${_('Code93')}>`,
								value: BCODER3.Code93,
							},
							{
								label: `<${_('Code128')}>`,
								value: BCODER3.Code128,
							},
							{
								label: `<${_('EANUCC128')}>`,
								value: BCODER3.EANUCC128,
							},
							{
								label: `<${_('POSTNET')}>`,
								value: BCODER3.Zip,
							},
						],
					},
					{
						label: _('BarcodeData'),
						inputName: 'Data',
						inputType: InputType.Select,
						inputValue: newObject.Data, // .ObjTags("SYS_BarMessage")
						labelTitle: _('BarcodeDataDesc'),
						opts: await pupulateCustomData({ customOpts, type: 3, udfs }),
					},
					{
						label: _('BarcodeComment'),
						inputName: 'Comment',
						inputType: InputType.Select,
						inputValue: newObject.Comment, // .ObjTags("SYS_BarComment")
						labelTitle: _('BarcodeCommentDesc'),
						opts: await pupulateCustomData({ customOpts, type: 4, udfs }),
					},
					{
						label: _('BarcodeSupplemental'),
						inputName: 'Supplemental',
						inputType: InputType.Select,
						inputValue: newObject.Supplemental, // .ObjTags("SYS_BarUPCSupplemental")
						labelTitle: _('BarcodeSupplementalDesc'),
						opts: await pupulateCustomData({ customOpts, type: 5, udfs }),
					},
					{
						label: _('NarrowBarWidth'),
						inputName: 'NarrowBarWidth',
						inputType: InputType.InputNumber,
						inputValue: newObject.NarrowBarWidth, // .ObjTags("SYS_BarNarrowBarWidth")
						labelTitle: _('NarrowBarWidthDesc'),
						limits: [1, 98],
					},
					{
						label: _('ReductionGain'),
						inputName: 'ReductionGain',
						inputType: InputType.Select,
						inputValue: newObject.ReductionGain, // .ObjTags("SYS_BarWidthReduction")
						labelTitle: _('ReductionGainDesc'),
						opts: getBarcodeReductionOpts(newObject.NarrowBarWidth),
					},
					{
						label: _('BarcodeHeight'),
						inputName: 'Height',
						inputType: InputType.InputNumber,
						inputValue: newObject.Height, // .ObjTags("SYS_BarCodeHeight")
						labelTitle: _('BarcodeHeightDesc'),
						limits: [0.1, 7.8],
						step: '0.1',
					},
					{
						label: _('NarrowWideRatio'),
						inputName: 'NarrowWideRatio',
						inputType: InputType.Select,
						inputValue: newObject.NarrowWideRatio, // .ObjTags("SYS_BarNarrowToWideRatio")
						labelTitle: _('NarrowWideRatioDesc'),
						opts: [
							{
								label: '2.0',
								value: 20,
							},
							{
								label: '2.1',
								value: 21,
							},
							{
								label: '2.2',
								value: 22,
							},
							{
								label: '2.3',
								value: 23,
							},
							{
								label: '2.4',
								value: 24,
							},
							{
								label: '2.5',
								value: 25,
							},
							{
								label: '2.6',
								value: 26,
							},
							{
								label: '2.7',
								value: 27,
							},
							{
								label: '2.8',
								value: 28,
							},
							{
								label: '2.9',
								value: 29,
							},
							{
								label: '3.0',
								value: 30,
							},
						],
					},
					{
						label: _('CommentTop'),
						inputName: 'CommentOnTop',
						inputType: InputType.Checkbox,
						inputValue: newObject.CommentOnTop, // .ObjTags("SYS_BarCommentOnTop")
						labelTitle: _('CommentTopDesc'),
					},
					{
						label: _('IncludeText'),
						inputName: 'IncludeText',
						inputType: InputType.Checkbox,
						inputValue: newObject.IncludeText, // .ObjTags("SYS_BarIncludeText")
					},
					{
						label: _('TextTop'),
						inputName: 'TextOnTop',
						inputType: InputType.Checkbox,
						inputValue: newObject.TextOnTop, // .ObjTags("SYS_BarTextOnTop")
					},
					{
						label: _('ShowQuietZones'),
						inputName: 'ShowQuietZones',
						inputType: InputType.Checkbox,
						inputValue: newObject.ShowQuietZones, // .ObjTags("SYS_BarIncludeQZ")
					},
					{
						label: _('ShowBearerBars'),
						inputName: 'ShowBearerBars',
						inputType: InputType.Checkbox,
						inputValue: newObject.ShowBearerBars, // .ObjTags("SYS_BarTextOnTop")
					},
					{
						label: _('ShowCheckCharacter'),
						inputName: 'ShowCheckCharacter',
						inputType: InputType.Checkbox,
						inputValue: newObject.ShowCheckCharacter, // .ObjTags("SYS_BarC39CHK")
					},
					{
						label: _('AppendLuhnCheckDigit'),
						inputName: 'AppendLuhnCheckDigit',
						inputType: InputType.Checkbox,
						inputValue: newObject.AppendLuhnCheckDigit, // .ObjTags("SYS_ShowLuhnCheckDigit")
						labelTitle: _('AppendLuhnCheckDigitDesc'),
					},
				]),
			]);

			break;
		}
		case C_PictureTypeCustom.MDOT_TEXT:
		case C_PictureTypeCustom.MDOT_DBTEXT: {
			const newObject = currentObject as GraphicText;
			if (isBadge) {
				let customOpts: string[] = [];
				const customOptsResp = await graphicApi.getCustomBadgeFields();
				if (customOptsResp.ResponseStatusCode === ResponseStatusCode.Success) {
					customOpts = customOptsResp.Entity;
				}

				/* let udfs: UserDefinedFieldSetup[] = [];
				const udfsResponse = await enrollmentApi.getUserDefinedFields();
				if (udfsResponse.ResponseStatusCode === ResponseStatusCode.Success) {
					udfs = udfsResponse.Entity;
				} */

				sectionsMap.set(_('Data'), [
					buildRow({
						label: _('DataField'),
						inputName: 'DataField',
						inputType: InputType.Select,
						inputValue: newObject.DataField, // .ObjTags("SYS_VDOData")
						labelTitle: _('DataFieldDesc'),
						opts: await pupulateCustomData({ customOpts, type: 1, udfs }),
					}),
				]);
				await getUDFObjectPropertyData(sectionsMap, newObject.UDF.toString(), udfs256);
			}

			sectionsMap.set(_('General'), [
				...(sectionsMap.get(_('General')) || []),
				buildRow({
					label: _('Text'),
					inputName: 'DisplayText',
					inputValue: newObject.FontInfo.DisplayText,
				}),
			]);

			// Retrieving font info
			const { Id, FontFamily, FontSize, FontStyle, TextColorHex, AutoSize } = newObject.FontInfo;
			sectionsMap.set(_('Appearance'), [
				...sectionsMap.get(_('Appearance')),
				...buildFontRows({
					fontInfo: {
						Id,
						FontFamily,
						FontSize,
						FontStyle,
						TextColorHex,
						AutoSize,
					},
					fontFamilies,
					isFontStyleEnabled: true,
					onChangeColorPicker,
				}),
			]);

			if (currentObject.ObjType === C_PictureTypeCustom.MDOT_TEXT) {
				sectionsMap.set(_('Appearance'), [
					...sectionsMap.get(_('Appearance')),
					buildRow({
						label: _('AutoSize'),
						inputName: 'AutoSize',
						inputType: InputType.Checkbox,
						inputValue: newObject.FontInfo.AutoSize, // .ObjTags("SYS_DBText_AutoSize")
					}),
				]);
			}

			break;
		}
		case C_PictureTypeCustom.MDOT_DBMAGSTRIPE: {
			const newObject = currentObject as BadgeStudioMagstripe;
			// Retrieving font info
			const { Id, MagstripeFont, MagstripeFontColor, MagstripeDisplayText } = newObject;
			sectionsMap.set(_('Appearance'), [
				...sectionsMap.get(_('Appearance')),
				...buildFontRows({
					fontInfo: {
						Id,
						FontStyle: 0,
						DisplayText: MagstripeDisplayText,
						FontFamily: MagstripeFont,
						TextColorHex: MagstripeFontColor,
					},
					fontFamilies,
					isFontStyleEnabled: false,
					onChangeColorPicker,
				}),
			]);

			// if (currentObject.SysVDO) { (Is VDO[BadgeObject]??)
			if (isBadge) {
				let customOpts: string[] = [];
				const customOptsResp = await graphicApi.getCustomBadgeFields();
				if (customOptsResp.ResponseStatusCode === ResponseStatusCode.Success) {
					customOpts = customOptsResp.Entity;
				}

				/* let udfs: UserDefinedFieldSetup[] = [];
				const udfsResponse = await enrollmentApi.getUserDefinedFields();
				if (udfsResponse.ResponseStatusCode === ResponseStatusCode.Success) {
					udfs = udfsResponse.Entity;
				} */

				sectionsMap.set(_('MagStripe'), [
					buildRow({
						label: _('MagstripeData'),
						inputName: 'MagstripesData',
						inputType: InputType.Select,
						inputValue: newObject.MagstripesData, // .ObjTags("SYS_VDOData")
						labelTitle: _('MagstripeDataDesc'),
						opts: await pupulateCustomData({ customOpts, type: 2, udfs }),
					}),
				]);
			}
			break;
		}
		case C_ObjectType.OT_IMAGE:
		case C_ObjectType.OT_BITMAP:
		case C_PictureTypeCustom.MDOT_DBIMAGE: {
			const newObject = currentObject as BadgeStudioImage;
			// .ObjTags("SYS_VDO")
			// if (currentObject.SysVDO) { (Is VDO[BadgeObject]??)
			if (isBadge) {
				const additionalOpts: DefaultOptionType[] = [];
				const imagePlaceHolderResponse = await graphicApi.getImagePlaceHolders();
				if (imagePlaceHolderResponse.ResponseStatusCode === ResponseStatusCode.Success && imagePlaceHolderResponse.Entity?.length > 0) {
					imagePlaceHolderResponse.Entity.forEach(img => {
						additionalOpts.push({
							label: img.Caption,
							value: img.ImagePlaceHolderId,
						});
					});
				}

				sectionsMap.set(_('Data'), [
					buildRow({
						label: _('DataField'),
						inputName: 'ImageType',
						inputType: InputType.Select,
						inputValue: newObject.ImageType, // .ObjTags("SYS_VDOData")
						labelTitle: _('DataFieldImgDesc'),
						opts: [
							{
								label: _('None'),
								value: 0,
							},
							{
								label: _('Photo'),
								value: -1,
							},
							{
								label: _('Signature'),
								value: -2,
							},
							...additionalOpts,
						],
					}),
				]);

				/* TODO: This is not going to be supported on web
				if (mdType === C_PictureTypeCustom.MDOT_DBIMAGE) {
					sectionsMap.set(_('Appearance'), [
						...(sectionsMap.get(_('Appearance')) || []),
						buildRow({
							label: _('Shape'),
							rowId: 'CircleMask',
							inputType: InputType.Select,
							inputValue: newObject, // .ObjTags("CircleMask")
							labelTitle: _('ShapeDesc'),
							opts: [
								{
									label: _('Rectangle'),
									value: 0,
								},
								{
									label: _('Circle'),
									value: 1,
								},
							],
						}),
					]);
				}
				*/
			}

			sectionsMap.set(_('Appearance'), [
				...(sectionsMap.get(_('Appearance')) || []),
				buildRow({
					label: _('Transparency'),
					inputName: 'Transparency',
					inputType: InputType.InputNumber,
					inputValue: newObject.AppearanceInfo.Transparency, // .ObjTags("Transparency")
					labelTitle: _('TransparencyDesc'),
					limits: [0, 255],
				}),
			]);

			break;
		}
		case C_PictureTypeCustom.MDOT_VIO: {
			// TODO: All this case is for Maps, Don't apply for BadgeStudio
			const newObject = currentObject as VelocityObject;
			// TODO: Check where to retrieve this two data
			const controllerId: number = 1;
			const iPointIndex: number = 1;
			sectionsMap.set(_('General'), [
				...(sectionsMap.get(_('General')) || []),
				...buildRows([
					{
						label: _('Address'),
						inputName: 'Address',
						inputValue: newObject.Address, // currentObject.Address
					},
					{
						label: _('Primary'),
						inputName: 'DefaultVO',
						inputValue: newObject.DefaultVO, // m_VOs.DefaultVO(m_VOs.IndexForKey(.ObjTags("SYS_VOKey")))
						labelTitle: _('PrimaryDesc'),
					},
				]),
			]);

			if (controllerId !== 0 && iPointIndex !== 0) {
				sectionsMap.set(_('General'), [
					...(sectionsMap.get(_('General')) || []),
					buildRow({
						label: _('LeftMouseClick'),
						inputName: 'SysClickAction',
						inputType: InputType.Select,
						inputValue: '', // TODO: .ObjTags("SYS_ClickAction")
						labelTitle: _('LeftMouseClick'),
						opts: [
							{
								label: _('None'),
								value: 0,
							},
							{
								label: `${_('Access')}: ${_('MomentaryAcess')}`,
								value: `${controllerId},CALL_API|DOORFUNCTION|MOMENTARY_ACCESS,${iPointIndex}`,
							},
							{
								label: `${_('Access')}: ${_('Unlock')}`,
								value: `${controllerId},CALL_API|DOORFUNCTION|UNLOCK_DOOR,${iPointIndex}`,
							},
							{
								label: `${_('Access')}: ${_('Relock')}`,
								value: `${controllerId},CALL_API|DOORFUNCTION|RELOCK_DOOR,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('Trigger')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|TRIGGER,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('Mask')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|MASK,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('UnMask')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|UNMASK,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('MomentaryMask')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|MOMENTARY_MASK,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('ForceON')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|FORCEON,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('ForceONRelease')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|FORCEON_RELEASE,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('ForceOFF')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|FORCEOFF,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('ForceOFFRelease')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|FORCEOFF_RELEASE,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('LockOPEN')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|LOCKOPEN,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('LockOPENRelease')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|LOCKOPEN_RELEASE,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('LockDOWN')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|LOCKDOWN,${iPointIndex}`,
							},
							{
								label: `${_('Control')}: ${_('LockDOWNRelease')}`,
								value: `${controllerId},CALL_API|CONTROLFUNCTION|LOCKDOWN_RELEASE,${iPointIndex}`,
							},
						],
					}),
				]);
			}

			break;
		}
		case C_PictureTypeCustom.MDOT_CONTROLZONE: {
			// TODO: Where to get ControlZone Info

			const asmuColorRows = buildASMUColorRows({
				// TODO: .ObjTags("SYS_CZ_AlarmColor")
				alarmColor: '',
				alarmId: 'SysCZAlarmColor',
				alarmTitle: _('AlarmColorDesc'),
				enableAlarm: true,
				enableMasked: true,
				enableSecure: true,
				enableUnknown: true,
				// TODO: .ObjTags("SYS_CZ_MaskedColor")
				maskedColor: '',
				maskedId: 'SysCZMaskedColor',
				maskedTitle: 'MaskedColorDesc',
				// TODO: .ObjTags("SYS_CZ_SecureColor")
				secureColor: '',
				secureId: 'SysCZSecureColor',
				secureTitle: _('SecureColorDesc'),
				// TODO: .ObjTags("SYS_CZ_UnknownColor")
				unknownColor: '',
				unknownId: 'SysCZUnknownColor',
				unknownTitle: _('UnknownColorDesc'),
			});

			const pCZAddressOps: DefaultOptionType[] = [];
			const controllersResponse = await graphicApi.getAllControllers();
			if (controllersResponse.ResponseStatusCode === ResponseStatusCode.Success && controllersResponse.Entity?.length > 0) {
				controllersResponse.Entity.forEach(controller => {
					pCZAddressOps.push({
						key: controller.Id,
						label: controller.Name,
						value: controller.Address,
					});
				});
			}

			const controllerId: number = 17; // TODO: Check where to get this value
			const pCZIDOps: DefaultOptionType[] = [];
			const controlZoneControllersResponse = await graphicApi.getControllerControlZonesByControllerId(controllerId);
			if (controlZoneControllersResponse.ResponseStatusCode === ResponseStatusCode.Success && controlZoneControllersResponse.Entity?.length > 0) {
				controlZoneControllersResponse.Entity.forEach(controller => {
					pCZIDOps.push({
						label: controller.Name,
						value: controller.Id,
					});
				});
			}

			sectionsMap.set(_('ControlZoneInformation'), [
				...buildRows([
					{
						label: _('Controller'),
						inputName: 'SysCZAddress',
						inputType: InputType.Select,
						inputValue: '', // TODO: .ObjTags("SYS_CZ_Address")
						labelTitle: _('ControlZoneControllerDesc'),
						opts: pCZAddressOps,
					},
					{
						label: _('ControlZone'),
						inputName: 'SysCZControlZoneID',
						inputType: InputType.Select,
						inputValue: '', // TODO: .ObjTags("SYS_CZ_ControlZoneID")
						labelTitle: _('ControlZoneDesc'),
						opts: pCZIDOps,
					},
				]),
				...asmuColorRows,
			]);

			break;
		}
		case C_PictureTypeCustom.MDOT_PMZ: {
			const newObject = currentObject as PointMonitoringZone;
			const asmuColorRows = buildASMUColorRows({
				alarmColor: newObject.AlarmColor, // .ObjTags("SYS_PMZ_AlarmColor")
				alarmId: 'AlarmColor',
				alarmTitle: _('AlarmColorPMZDesc'),
				enableAlarm: true,
				enableMasked: true,
				enableSecure: true,
				enableUnknown: true,
				maskedColor: newObject.MaskedColor, // .ObjTags("SYS_PMZ_MaskedColor")
				maskedId: 'MaskedColor',
				maskedTitle: 'MaskedColorDesc',
				secureColor: newObject.SecureColor, // .ObjTags("SYS_PMZ_SecureColor")
				secureId: 'SecureColor',
				secureTitle: _('SecureColorDesc'),
				unknownColor: newObject.UnknownColor, // .ObjTags("SYS_PMZ_UnknownColor")
				unknownId: 'UnknownColor',
				unknownTitle: _('UnknownColorDesc'),
			});
			sectionsMap.set(_('PointMonitoringZone'), [
				buildRow({
					label: _('SelectPoints'),
					inputName: 'pAddPMZPoints', // TODO: Check how to implement logic to add Point Monitoring Zones
					labelTitle: _('SelectPointsDesc'),
				}),
				...asmuColorRows,
			]);
			break;
		}

		/* TODO: Where to get this type of Object
			case C_PictureTypeCustom.MDOT_IDS_AREA: {
				const getIDSAreasResponse = await graphicApi.getIDSAreas();
				const opts: DefaultOptionType[] = [];
				if (getIDSAreasResponse.ResponseStatusCode === ResponseStatusCode.Success && getIDSAreasResponse.Entity?.length > 0) {
					getIDSAreasResponse.Entity.map(idsArea => {
						opts.push({
							label: idsArea.AreaName,
							value: idsArea.AreaId,
						});
					});
				}

				sectionsMap.set(_('IDSArea'), [
					buildRow({
						label: _('SelectArea'),
						rowId: 'SysIDSAREAAreaID',
						inputType: InputType.Select,
						inputValue: '', // TODO: .ObjTags("SYS_IDSAREA_AreaID")
						labelTitle: _('SelectAreaDesc'),
						opts,
					}),
				]);

				// TODO: If Not (.ObjTags("SYS_IDSAREA_PluginID") = -1 Or IsEmpty(.ObjTags("SYS_IDSAREA_PluginID"))) Then
				var tempNumber = 1;
				var tempString = '';
				if (!(tempNumber === -1 || !tempNumber)) {
					let secureTitle: string = '';
					// TODO: If (.ObjTags("SYS_IDSArea_AlarmColor") = -1 Or IsEmpty(.ObjTags("SYS_IDSArea_AlarmColor"))) Then
					if (!tempString || tempString === '-1') {
						// TODO: .ObjTags("SYS_IDSArea_AlarmColor") = 255
						// currentObject.SysIDSAreaAlarmColor = '#FFFFFF';
					}

					// TODO:  If (.ObjTags("SYS_IDSArea_SecureColor") = -1 Or IsEmpty(.ObjTags("SYS_IDSArea_SecureColor"))) Then
					if (!tempString || tempString === '-1') {
						// TODO: .ObjTags("SYS_IDSArea_SecureColor") = 8454016
						// currentObject.SysIDSAreaSecureColor = '';
					} else {
						secureTitle = _('SecureColorIDSDesc');
					}

					const asmuColorRows = buildASMUColorRows({
						// TODO: .ObjTags("SYS_IDSArea_AlarmColor")
						alarmColor: '',
						alarmId: 'SysIDSAreaAlarmColor',
						alarmTitle: _('AlarmColorIDSDesc'),
						enableAlarm: true,
						enableSecure: true,
						// TODO: .ObjTags("pIDSAreaSecureColor")
						secureColor: '',
						secureId: 'SysIDSAreaSecureColor',
						secureTitle,
					});

					sectionsMap.set(_('IDSArea'), [...sectionsMap.get(_('IDSArea')), ...asmuColorRows]);

					/*
					TODO: rst.Open "Select * from IDS_ArmStates where PluginID = " & .ObjTags("SYS_IDSAREA_PluginID") & " Order By ArmStateId", m_Con, adOpenForwardOnly, adLockReadOnly
					TODO: Add extra items with this logic, is this logic still in use? IDS_ArmStates table does not exist
					For i = 0 To UBound(data, 2)
						'LogThis "Loading Arm States: " & data(2, i)
						If (.ObjTags("SYS_IDSArea_ArmColor_" & data(0, i)) = -1 Or IsEmpty(.ObjTags("SYS_IDSArea_ArmColor_" & data(0, i)))) Then
							' if no tag yet, load defaults from database
							uProps.AddProperty "pIDSAreaArmColor_" & data(0, i), data(2, i) & " Color", "IDS Area Arm Colors", PROP_DT_COLOR, data(3, i), , "Border Color for Area when state is " & data(2, i)
							bSkipLoadProps = True
							' set the color in the object in the map
							.ObjTags("SYS_IDSArea_ArmColor_" & data(0, i)) = data(3, i)
							bSkipLoadProps = False
						Else
							' if the tag exists, load the property grid with the value embedded in the object
							uProps.AddProperty "pIDSAreaArmColor_" & data(0, i), data(2, i) & " Color", "IDS Area Arm Colors", PROP_DT_COLOR, .ObjTags("SYS_IDSArea_ArmColor_" & data(0, i)), , "Border Color for Area when state is " & data(2, i)

						End If
					Next i
					*
				}
				break;
		} */
		default:
			const sysUDF: string = '';
			await getUDFObjectPropertyData(sectionsMap, sysUDF, udfs256);
	}

	sectionsMap.set(_('General'), [
		...sectionsMap.get(_('General')),
		...buildRows([
			{
				label: _('Position'),
				inputName: 'positionTitle',
				isTitleRow: true,
			},
			{
				inputType: InputType.InputNumber,
				inputValue: tempGenericObject.PositionInfo.Top, // .ObjTop
				label: _('Top'),
				inputName: ['PositionInfo', 'Top'],
				labelTitle: _('TopDesc'),
			},
			{
				inputType: InputType.InputNumber,
				inputValue: tempGenericObject.PositionInfo.Bottom, // .ObjBottom
				label: _('Bottom'),
				inputName: ['PositionInfo', 'Bottom'],
				labelTitle: _('BottomDesc'),
			},
			{
				inputType: InputType.InputNumber,
				inputValue: tempGenericObject.PositionInfo.Left, // .ObjLeft
				label: _('Left'),
				inputName: ['PositionInfo', 'Left'],
				labelTitle: _('LeftDesc'),
			},
			{
				inputType: InputType.InputNumber,
				inputValue: tempGenericObject.PositionInfo.Right, // .ObjRight
				label: _('Right'),
				inputName: ['PositionInfo', 'Right'],
				labelTitle: _('RightDesc'),
			},
			{
				inputType: InputType.InputNumber,
				inputValue: tempGenericObject.PositionInfo.Rotation, // .ObjRotation
				label: _('Rotation'),
				inputName: ['PositionInfo', 'Rotation'],
				labelTitle: _('RotationDesc'),
			},
		]),
	]);

	/*
	if (currentObject.ObjType !== C_PictureTypeCustom.MDOT_DBBARCODE) {
		sectionsMap.set(_('Appearance'), [
			...sectionsMap.get(_('Appearance')),
			buildRow({
				label: _('Line'),
				inputName: 'pLineColorAndWidthTitle',
				isTitleRow: true,
			}),
			<Row gutter={8} key='pLineColorAndWidth'>
				<Col className={styles.colFlex} span={13}>
					<Form.Item
						className={styles.formItemColorPicker}
						name='LineColor'
						rules={[{ required: true, message: 'Required field' }]}
						validateTrigger='onBlur'
					>
						{/* TODO: .LineColor *}
						<CustomColorPicker name={['LineColor']} initialColor={''} onChangeColorPicker={onChangeColorPicker} />
					</Form.Item>
				</Col>
				<Col className={styles.colFlex} span={11}>
					<LineWeight style={{ fontSize: 32 }} />
					{/* TODO: .LineWidth *}
					<Form.Item initialValue={''} name='LineWidth' rules={[{ required: true, message: 'Required field' }]} validateTrigger='onBlur'>
						<InputNumber max={10} min={1} />
					</Form.Item>
				</Col>
			</Row>,
			<Row gutter={8} key='pLineStyle'>
				<Col span={24}>
					{/* TODO: .LineStyle *}
					<Form.Item name='LineStyle' initialValue={''} rules={[{ required: true, message: 'Required field' }]} validateTrigger='onBlur'>
						<Select>
							<Select.Option key={-1} value={-1}>{`<${_('Various')}>`}</Select.Option>
							<Select.Option key={5} value={5}>{`<${_('None')}>`}</Select.Option>
							<Select.Option key={0} value={0}>
								{_('Solid')}
							</Select.Option>
							<Select.Option key={1} value={1}>
								{_('Dash')}
							</Select.Option>
							<Select.Option key={2} value={2}>
								{_('Dot')}
							</Select.Option>
							<Select.Option key={3} value={3}>
								{_('DashDot')}
							</Select.Option>
							<Select.Option key={4} value={4}>
								{_('DashDotDot')}
							</Select.Option>
							<Select.Option key={6} value={6}>
								{_('Inside')}
							</Select.Option>
						</Select>
					</Form.Item>
				</Col>
			</Row>,
			<Row gutter={8} className={styles.rowTitle} key='pFillTitle'>
				<span>{_('Fill')}</span>
			</Row>,
			<Row gutter={8} key='pFillColor'>
				{/* TODO: .FillColor *}
				<Col className={styles.colFlex} span={13}>
					<Form.Item className={styles.formItemColorPicker} name='FillColor' rules={[{ required: true, message: 'Required field' }]}>
						<CustomColorPicker name={['FillColor']} initialColor={''} onChangeColorPicker={onChangeColorPicker} />
					</Form.Item>
				</Col>
			</Row>,
			<Row gutter={8} key='pFillStyle'>
				<Col span={24}>
					{/* TODO: .FillStyle *}
					<Form.Item name='FillStyle' initialValue={''} rules={[{ required: true, message: 'Required field' }]} validateTrigger='onBlur'>
						<Select>
							<Select.Option key={-1} value={-1}>{`<${_('Various')}>`}</Select.Option>
							<Select.Option key={1} value={1}>{`<${_('None')}>`}</Select.Option>
							<Select.Option key={0} value={0}>
								{_('Solid')}
							</Select.Option>
							<Select.Option key={2} value={2}>
								{_('Horizontal')}
							</Select.Option>
							<Select.Option key={3} value={3}>
								{_('Vertical')}
							</Select.Option>
							<Select.Option key={4} value={4}>
								{_('DiagonalBack')}
							</Select.Option>
							<Select.Option key={5} value={5}>
								{_('DiagonalForward')}
							</Select.Option>
							<Select.Option key={6} value={6}>
								{_('Cross')}
							</Select.Option>
							<Select.Option key={7} value={7}>
								{_('DiagonalCross')}
							</Select.Option>
						</Select>
					</Form.Item>
				</Col>
			</Row>,
			<Row gutter={8} className={styles.rowTitle} key='pBackTitle'>
				<span>{_('Back')}</span>
			</Row>,
			<Row gutter={8} key='pBackColor'>
				<Col className={styles.colFlex} span={13}>
					{/* TODO: .BackColor *}
					<Form.Item
						className={styles.formItemColorPicker}
						name='BackColor'
						rules={[{ required: true, message: 'Required field' }]}
						validateTrigger='onBlur'
					>
						<CustomColorPicker name={['BackColor']} initialColor={''} onChangeColorPicker={onChangeColorPicker} />
					</Form.Item>
				</Col>
			</Row>,
			<Row gutter={8} key='pBackStyle'>
				<Col span={24}>
					{/* TODO: .BackStyle *}
					<Form.Item initialValue={''} name='BackStyle' rules={[{ required: true, message: 'Required field' }]} validateTrigger='onBlur'>
						<Select>
							<Select.Option key={0} value={0}>
								{_('Opaque')}
							</Select.Option>
							<Select.Option key={1} value={1}>
								{_('Transparent')}
							</Select.Option>
						</Select>
					</Form.Item>
				</Col>
			</Row>,
		]);
	}
	*/
};

export const isFontAvailable = (fontName: string): boolean => {
	let canvas: HTMLCanvasElement = document.getElementById('fontCheckerCanvas') as HTMLCanvasElement;
	if (!canvas) {
		canvas = document.createElement('canvas');
	}
	const context = canvas.getContext('2d');
	if (!context) {
		return false;
	}

	const testString = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

	context.font = '12px monospace';
	const baselineSize = context.measureText(testString).width;

	context.font = `12px '${fontName}', monospace`;
	const newSize = context.measureText(testString).width;

	return newSize !== baselineSize;
};
